import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="comment-formatter"
export default class extends Controller {
  static targets = ["textarea", "wordCount", "emojiPicker", "emojiButton"];

  connect() {
    this.updateWordCount();
    // Lazy-load emoji picker element and mount inside target container
    this.ensureEmojiPicker();
    // Close emoji picker when clicking outside
    this._boundOutsideClick = this.closeEmojiPickerOnOutsideClick.bind(this);
    document.addEventListener("click", this._boundOutsideClick);
  }

  disconnect() {
    if (this._boundOutsideClick) {
      document.removeEventListener("click", this._boundOutsideClick);
      this._boundOutsideClick = null;
    }
  }

  updateWordCount() {
    const text = this.textareaTarget.value.trim();
    const wordCount = text === "" ? 0 : text.split(/\s+/).length;
    this.wordCountTarget.textContent = `${wordCount} word${
      wordCount !== 1 ? "s" : ""
    }`;
  }

  // Formatting methods
  toggleBold() {
    this.wrapSelection("**", "**");
  }

  toggleItalic() {
    this.wrapSelection("_", "_");
  }

  toggleStrike() {
    this.wrapSelection("~~", "~~");
  }

  toggleQuote() {
    this.wrapSelection("> ", "", true); // Line prefix
  }

  toggleCode() {
    this.wrapSelection("`", "`");
  }

  toggleSpoiler() {
    this.wrapSelection("[spoiler]", "[/spoiler]");
  }

  // Emoji functionality
  toggleEmoji() {
    this.ensureEmojiPicker();
    this.emojiPickerTarget.classList.toggle("hidden");
  }

  // Insert emoji from web component event
  insertEmojiUnicode(unicode) {
    this.insertAtCursor(unicode);
    this.emojiPickerTarget.classList.add("hidden");
  }

  closeEmojiPickerOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.emojiPickerTarget.classList.add("hidden");
    }
  }

  // Helper methods
  async ensureEmojiPicker() {
    if (this._pickerInitialized) return;
    try {
      // Dynamically import to keep initial bundle light
      await import("emoji-picker-element");
      const picker = document.createElement("emoji-picker");
      picker.className = "text-base";
      picker.addEventListener("emoji-click", (e) => {
        const unicode = e.detail?.unicode || e.detail?.emoji?.unicode;
        if (unicode) this.insertEmojiUnicode(unicode);
      });
      this.emojiPickerTarget.replaceChildren(picker);
      this._pickerInitialized = true;
    } catch (e) {
      // Fail silently; keep UX functional without picker
      // eslint-disable-next-line no-console
      console.error("Failed to load emoji-picker-element", e);
    }
  }
  wrapSelection(prefix, suffix, linePrefix = false) {
    const textarea = this.textareaTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    let newText;
    if (linePrefix) {
      // For line prefixes like quotes, add to beginning of each line
      const lines = selectedText.split("\n");
      const prefixedLines = lines.map((line) =>
        line.trim() ? prefix + line : line
      );
      newText = prefixedLines.join("\n");
    } else {
      // Regular wrapping
      if (selectedText) {
        newText = prefix + selectedText + suffix;
      } else {
        newText = prefix + suffix;
      }
    }

    textarea.value = beforeText + newText + afterText;

    // Update cursor position
    const newCursorPos = linePrefix
      ? start + newText.length
      : start + prefix.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);

    textarea.focus();
    this.updateWordCount();

    // Trigger character counter update
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
  }

  insertAtCursor(text) {
    const textarea = this.textareaTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    textarea.value = beforeText + text + afterText;
    textarea.setSelectionRange(start + text.length, start + text.length);
    textarea.focus();
    this.updateWordCount();

    // Trigger character counter update
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
